import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar, Footer } from './HomePage';
import api from '../utils/api';

const GATEWAY_ICONS = {
  stripe: '💳',
  paypal: '🅿️',
  razorpay: '₹',
};

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentModal, setPaymentModal] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    api.plans().then(d => { if (d.success) setPlans(d.plans); setLoading(false); });
  }, []);

  const handleChoosePlan = (plan) => {
    if (plan.slug === 'free') {
      if (!user) navigate('/register');
      return;
    }
    if (!user) { navigate('/login'); return; }
    setPaymentModal(plan);
  };

  const handlePayment = async (gateway) => {
    if (!paymentModal) return;
    setProcessingPayment(true);
    try {
      const data = await api.createOrder(paymentModal.id, gateway, yearly ? 'yearly' : 'monthly');
      if (data.error) { setMessage({ type: 'error', text: data.error }); return; }

      if (gateway === 'stripe' && data.redirect_url) {
        window.location.href = data.redirect_url;
      } else if (gateway === 'razorpay' && data.order_id) {
        // Load Razorpay script
        if (!window.Razorpay) {
          await new Promise(resolve => {
            const s = document.createElement('script');
            s.src = 'https://checkout.razorpay.com/v1/checkout.js';
            s.onload = resolve;
            document.head.appendChild(s);
          });
        }
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: 'INR',
          name: 'PhysicsAI Tutor',
          description: `${paymentModal.name} Plan`,
          order_id: data.order_id,
          handler: async (resp) => {
            await api.verifyPayment(resp.razorpay_payment_id, paymentModal.id, 'razorpay');
            setMessage({ type: 'success', text: 'Payment successful! Your plan has been upgraded.' });
            setPaymentModal(null);
          },
        };
        new window.Razorpay(options).open();
      } else if (gateway === 'paypal' && data.approve_url) {
        window.location.href = data.approve_url;
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Payment failed. Please try again.' });
    }
    setProcessingPayment(false);
  };

  const FAQ = [
    { q: 'Can I cancel anytime?', a: 'Yes, you can cancel your subscription at any time. You will continue to have access until the end of your billing period.' },
    { q: 'Is there a free trial?', a: 'Yes! The free plan includes 10 queries per month. No credit card required to start.' },
    { q: 'What payment methods are accepted?', a: 'We accept all major credit/debit cards via Stripe, PayPal, and Razorpay (for Indian users).' },
    { q: 'Can I upgrade or downgrade my plan?', a: 'Yes, you can change your plan at any time from your dashboard.' },
  ];

  return (
    <div>
      <Navbar user={user} />
      <section className="pricing-page">
        <div className="section-header" style={{ paddingTop: 20 }}>
          <h2 style={{ fontSize: 42 }}>Simple, Transparent Pricing</h2>
          <p>Start free, upgrade when you need more. All plans include access to our AI physics tutor.</p>
        </div>

        {message && (
          <div className={`alert alert-${message.type}`} style={{ maxWidth: 600, margin: '0 auto 24px' }}>
            {message.text}
          </div>
        )}

        <div className="billing-toggle">
          <span className="toggle-label">Monthly</span>
          <div className={`toggle-switch ${yearly ? 'on' : ''}`} onClick={() => setYearly(!yearly)} />
          <span className="toggle-label">Yearly</span>
          <span className="save-badge">Save 20%</span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading plans...</div>
        ) : (
          <div className="plans-grid">
            {plans.map((plan, i) => (
              <div key={plan.id} className={`plan-card ${plan.slug === 'pro' ? 'popular' : ''}`}>
                {plan.slug === 'pro' && <div className="popular-badge">⭐ Most Popular</div>}
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price">
                  {plan.slug === 'free' ? (
                    <><sup>$</sup>0<span className="period">/mo</span></>
                  ) : (
                    <>
                      <sup>$</sup>
                      {yearly ? plan.price_yearly : plan.price_monthly}
                      <span className="period">/{yearly ? 'yr' : 'mo'}</span>
                    </>
                  )}
                </div>
                <div className="plan-desc">
                  {plan.queries_limit === -1 ? 'Unlimited queries' : `${plan.queries_limit} queries/month`}
                </div>
                <ul className="plan-features">
                  {(plan.features || []).map((f, j) => <li key={j}>{f}</li>)}
                </ul>
                <button
                  className="btn-primary"
                  style={{ width: '100%', justifyContent: 'center', padding: '13px', fontSize: 15, borderRadius: 10, cursor: 'pointer' }}
                  onClick={() => handleChoosePlan(plan)}
                >
                  {plan.slug === 'free' ? 'Start Free' : user?.plan === plan.slug ? 'Current Plan ✓' : 'Get Started'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Payment comparison */}
        <div style={{ textAlign: 'center', margin: '20px 0 60px' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>Secure payments via:</p>
          <div className="payment-gateways">
            <div className="gateway-btn stripe">💳 Stripe</div>
            <div className="gateway-btn paypal">🅿️ PayPal</div>
            <div className="gateway-btn razorpay">₹ Razorpay</div>
          </div>
        </div>

        {/* FAQ */}
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h3 style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, color: 'var(--secondary)', marginBottom: 32 }}>Frequently Asked Questions</h3>
          {FAQ.map((f, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '20px 24px', marginBottom: 12, border: '1px solid var(--border)' }}>
              <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: 'var(--secondary)' }}>{f.q}</h4>
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Payment Modal */}
      {paymentModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 40, maxWidth: 460, width: '100%' }}>
            <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: 'var(--secondary)' }}>Choose Payment Method</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 14 }}>
              {paymentModal.name} Plan — ${yearly ? paymentModal.price_yearly : paymentModal.price_monthly}/{yearly ? 'yr' : 'mo'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {['stripe', 'paypal', 'razorpay'].map(gw => (
                <button key={gw} className={`gateway-btn ${gw}`}
                  style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: 15, fontWeight: 700 }}
                  onClick={() => handlePayment(gw)} disabled={processingPayment}>
                  {GATEWAY_ICONS[gw]} Pay with {gw.charAt(0).toUpperCase() + gw.slice(1)}
                </button>
              ))}
            </div>
            <button onClick={() => setPaymentModal(null)} style={{ width: '100%', padding: '11px', border: '2px solid var(--border)', borderRadius: 10, cursor: 'pointer', background: 'white', color: 'var(--text-muted)' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
